import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BODY_REGIONS, intensityColor, intensityLabel } from "../data/courseData";
import { Slider } from "./ui/slider";

// X-ray style anatomical body, head-to-toe, with visible spine, ribcage, pelvis.
// Tap = open intensity panel · Hold = ramp intensity, release to commit.

// ----- Region hit-boxes (anatomically positioned) -----
const FRONT_REGION_PATHS = {
  jaw: { type: "ellipse", cx: 100, cy: 42, rx: 16, ry: 7 },
  "neck-front": { type: "rect", x: 90, y: 54, w: 20, h: 16, r: 6 },
  "trap-l": { type: "ellipse", cx: 82, cy: 72, rx: 8, ry: 5 },
  "trap-r": { type: "ellipse", cx: 118, cy: 72, rx: 8, ry: 5 },
  "shoulder-l": { type: "ellipse", cx: 66, cy: 84, rx: 14, ry: 12 },
  "shoulder-r": { type: "ellipse", cx: 134, cy: 84, rx: 14, ry: 12 },
  chest: { type: "rect", x: 76, y: 80, w: 48, h: 44, r: 16 },
  abdomen: { type: "rect", x: 80, y: 126, w: 40, h: 42, r: 14 },
  "hip-l": { type: "ellipse", cx: 82, cy: 184, rx: 14, ry: 12 },
  "hip-r": { type: "ellipse", cx: 118, cy: 184, rx: 14, ry: 12 },
  "thigh-l": { type: "rect", x: 70, y: 198, w: 22, h: 70, r: 11 },
  "thigh-r": { type: "rect", x: 108, y: 198, w: 22, h: 70, r: 11 },
  "knee-l": { type: "ellipse", cx: 81, cy: 280, rx: 10, ry: 8 },
  "knee-r": { type: "ellipse", cx: 119, cy: 280, rx: 10, ry: 8 },
};

const BACK_REGION_PATHS = {
  "neck-back": { type: "rect", x: 90, y: 54, w: 20, h: 16, r: 6 },
  "trap-l-back": { type: "ellipse", cx: 82, cy: 74, rx: 10, ry: 6 },
  "trap-r-back": { type: "ellipse", cx: 118, cy: 74, rx: 10, ry: 6 },
  "upper-back": { type: "rect", x: 76, y: 78, w: 48, h: 30, r: 12 },
  "mid-back": { type: "rect", x: 80, y: 112, w: 40, h: 28, r: 12 },
  "lower-back": { type: "rect", x: 82, y: 146, w: 36, h: 28, r: 10 },
  "glute-l": { type: "ellipse", cx: 86, cy: 192, rx: 14, ry: 13 },
  "glute-r": { type: "ellipse", cx: 114, cy: 192, rx: 14, ry: 13 },
  "hamstring-l": { type: "rect", x: 72, y: 210, w: 20, h: 58, r: 10 },
  "hamstring-r": { type: "rect", x: 108, y: 210, w: 20, h: 58, r: 10 },
  "calf-l": { type: "rect", x: 74, y: 280, w: 16, h: 42, r: 9 },
  "calf-r": { type: "rect", x: 110, y: 280, w: 16, h: 42, r: 9 },
};

// ----- Skeleton overlay -----
function FrontSkeleton() {
  return (
    <g filter="url(#strongGlow)">
      {/* Lumbar pain hot-zone glow */}
      <ellipse cx="100" cy="172" rx="26" ry="36" fill="url(#lumbarHot)" style={{ pointerEvents: "none" }} />
      {/* Spine column (visible through translucent torso) */}
      <g stroke="rgba(34,211,238,0.85)" strokeWidth="1" fill="rgba(34,211,238,0.45)">
        {/* Cervical 7 */}
        {[60, 64, 68].map((y) => (<rect key={`c${y}`} x="97.5" y={y} width="5" height="3" rx="1" />))}
        {/* Thoracic 12 */}
        {[74, 80, 86, 92, 98, 104, 110, 116, 122, 128, 134, 140].map((y) => (<rect key={`t${y}`} x="96.5" y={y} width="7" height="4.2" rx="1.3" />))}
        {/* Lumbar 5 — pain-highlighted orange */}
        {[148, 156, 164, 172, 180].map((y) => (<rect key={`l${y}`} x="95.5" y={y} width="9" height="5.5" rx="1.6" fill="rgba(249,140,30,0.95)" stroke="rgba(255,210,120,0.85)" strokeWidth="0.5" />))}
        {/* Sacrum */}
        <path d="M 95 188 Q 100 198 105 188 L 103 200 Q 100 204 97 200 Z" fill="rgba(249,115,22,0.9)" stroke="rgba(255,210,120,0.7)" strokeWidth="0.5" />
      </g>

      {/* Ribcage — paired arcs from spine to sides (translucent through body) */}
      <g stroke="rgba(34,211,238,0.55)" strokeWidth="0.75" fill="none" strokeLinecap="round">
        {[80, 88, 96, 104, 112, 120, 128].map((y, i) => {
          const spread = 22 + i * 1.4;
          return (
            <g key={`rib-${y}`}>
              <path d={`M 100 ${y} Q ${100 - spread} ${y + 4} ${100 - spread - 4} ${y + 12}`} />
              <path d={`M 100 ${y} Q ${100 + spread} ${y + 4} ${100 + spread + 4} ${y + 12}`} />
            </g>
          );
        })}
        {/* Sternum */}
        <line x1="100" y1="78" x2="100" y2="132" strokeDasharray="2 2.5" stroke="rgba(34,211,238,0.4)" />
      </g>

      {/* Clavicles */}
      <g stroke="rgba(34,211,238,0.6)" strokeWidth="1" fill="none" strokeLinecap="round">
        <path d="M 100 72 Q 84 76 64 80" />
        <path d="M 100 72 Q 116 76 136 80" />
      </g>

      {/* Pelvis / iliac wings */}
      <g stroke="rgba(34,211,238,0.7)" strokeWidth="0.85" fill="rgba(34,211,238,0.08)">
        <path d="M 74 178 Q 70 196 78 208 L 96 200 Q 100 196 100 188 Q 100 196 104 200 L 122 208 Q 130 196 126 178 Q 116 190 100 190 Q 84 190 74 178 Z" />
        {/* Hip sockets (subtle) */}
        <circle cx="80" cy="200" r="3" fill="rgba(3,16,31,0.6)" stroke="rgba(34,211,238,0.6)" strokeWidth="0.7" />
        <circle cx="120" cy="200" r="3" fill="rgba(3,16,31,0.6)" stroke="rgba(34,211,238,0.6)" strokeWidth="0.7" />
      </g>

      {/* Limb bones (inner) */}
      <g stroke="rgba(34,211,238,0.35)" strokeWidth="0.6" fill="none">
        {/* Arms — humerus */}
        <path d="M 64 86 L 58 142" />
        <path d="M 136 86 L 142 142" />
        {/* Radius / ulna */}
        <path d="M 58 142 L 55 184" />
        <path d="M 60 142 L 62 184" />
        <path d="M 142 142 L 145 184" />
        <path d="M 140 142 L 138 184" />
        {/* Femur */}
        <path d="M 84 200 L 80 274" />
        <path d="M 116 200 L 120 274" />
        {/* Tibia / fibula */}
        <path d="M 80 286 L 78 326" />
        <path d="M 84 286 L 84 326" />
        <path d="M 120 286 L 122 326" />
        <path d="M 116 286 L 116 326" />
      </g>
    </g>
  );
}

function BackSkeleton() {
  return (
    <g filter="url(#strongGlow)">
      {/* Lumbar pain hot-zone glow */}
      <ellipse cx="100" cy="174" rx="26" ry="36" fill="url(#lumbarHot)" style={{ pointerEvents: "none" }} />
      {/* Spine column — bright on back view */}
      <g>
        {/* Cervical */}
        {[60, 64, 68].map((y) => (<rect key={`c${y}`} x="96.5" y={y} width="7" height="3.4" rx="1.4" fill="rgba(34,211,238,0.85)" stroke="rgba(255,255,255,0.6)" strokeWidth="0.4" />))}
        {/* Thoracic */}
        {[74, 80, 86, 92, 98, 104, 110, 116, 122, 128, 134, 140].map((y) => (<rect key={`t${y}`} x="95" y={y} width="10" height="4.4" rx="1.6" fill="rgba(34,211,238,0.92)" stroke="rgba(255,255,255,0.7)" strokeWidth="0.5" />))}
        {/* Lumbar — pain-highlighted orange */}
        {[148, 156, 164, 172, 180].map((y) => (<rect key={`l${y}`} x="93" y={y} width="14" height="5.8" rx="1.8" fill="rgba(249,140,30,0.96)" stroke="rgba(255,210,120,0.9)" strokeWidth="0.6" />))}
        {/* Sacrum / coccyx */}
        <path d="M 92 188 Q 100 202 108 188 L 105 204 Q 100 210 95 204 Z" fill="rgba(249,115,22,0.92)" stroke="rgba(255,210,120,0.7)" strokeWidth="0.5" />
      </g>

      {/* Shoulder blades */}
      <g stroke="rgba(34,211,238,0.6)" strokeWidth="0.85" fill="rgba(34,211,238,0.08)">
        <path d="M 72 80 Q 64 100 76 122 L 92 110 Q 90 90 84 78 Z" />
        <path d="M 128 80 Q 136 100 124 122 L 108 110 Q 110 90 116 78 Z" />
      </g>

      {/* Rib hints (back) */}
      <g stroke="rgba(34,211,238,0.32)" strokeWidth="0.6" fill="none" strokeLinecap="round">
        {[88, 96, 104, 112, 120, 128].map((y, i) => {
          const spread = 18 + i * 1.4;
          return (
            <g key={`rb-${y}`}>
              <path d={`M 100 ${y} Q ${100 - spread} ${y + 6} ${100 - spread - 2} ${y + 14}`} />
              <path d={`M 100 ${y} Q ${100 + spread} ${y + 6} ${100 + spread + 2} ${y + 14}`} />
            </g>
          );
        })}
      </g>

      {/* Pelvis (back view) */}
      <g stroke="rgba(34,211,238,0.7)" strokeWidth="0.85" fill="rgba(34,211,238,0.08)">
        <path d="M 72 180 Q 68 200 80 212 L 100 204 L 120 212 Q 132 200 128 180 Q 116 196 100 196 Q 84 196 72 180 Z" />
      </g>

      {/* Limb bones */}
      <g stroke="rgba(34,211,238,0.35)" strokeWidth="0.6" fill="none">
        <path d="M 64 86 L 58 142" />
        <path d="M 136 86 L 142 142" />
        <path d="M 58 142 L 55 184" />
        <path d="M 142 142 L 145 184" />
        <path d="M 84 204 L 80 274" />
        <path d="M 116 204 L 120 274" />
        <path d="M 80 286 L 78 326" />
        <path d="M 120 286 L 122 326" />
      </g>
    </g>
  );
}

function BodySilhouette({ side, regions, selected, activeId, holdState, handlers }) {
  const map = side === "front" ? FRONT_REGION_PATHS : BACK_REGION_PATHS;
  return (
    <svg
      viewBox="0 0 200 350"
      className="w-full h-auto select-none touch-none"
      data-testid={`body-silhouette-${side}`}
    >
      <defs>
        <radialGradient id="bodyVolume" cx="35%" cy="22%" r="85%">
          <stop offset="0%" stopColor="rgba(180,240,255,0.30)" />
          <stop offset="30%" stopColor="rgba(56,189,248,0.34)" />
          <stop offset="70%" stopColor="rgba(12,74,140,0.55)" />
          <stop offset="100%" stopColor="rgba(3,16,31,1)" />
        </radialGradient>
        <linearGradient id="rimLight" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgba(120,230,255,1)" />
          <stop offset="50%" stopColor="rgba(56,189,248,0.85)" />
          <stop offset="100%" stopColor="rgba(34,211,238,1)" />
        </linearGradient>
        <radialGradient id="headGloss" cx="40%" cy="30%" r="65%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="40%" stopColor="rgba(34,211,238,0.25)" />
          <stop offset="100%" stopColor="rgba(5,11,26,0.95)" />
        </radialGradient>
        <filter id="bodyGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="strongGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id="lumbarHot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255,180,40,0.55)" />
          <stop offset="55%" stopColor="rgba(249,115,22,0.28)" />
          <stop offset="100%" stopColor="rgba(249,115,22,0)" />
        </radialGradient>
      </defs>

      {/* Floor reflection */}
      <ellipse cx="100" cy="338" rx="52" ry="4" fill="rgba(34,211,238,0.10)" />

      {/* --- Body silhouette outline + fill (translucent x-ray skin) --- */}
      <g filter="url(#bodyGlow)">
        {/* Head */}
        <ellipse cx="100" cy="32" rx="22" ry="25" fill="url(#headGloss)" stroke="url(#rimLight)" strokeWidth="0.9" />
        {/* Cranium contour line */}
        <path d="M 80 26 Q 100 16 120 26" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.5" />
        {/* Jaw hint */}
        <ellipse cx="100" cy="48" rx="14" ry="5" fill="rgba(3,16,31,0.3)" />

        {/* Neck */}
        <path d="M 90 54 L 110 54 L 113 68 L 87 68 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.8" strokeLinejoin="round" />

        {/* Torso */}
        <path
          d="M 70 74 Q 60 80 58 96 L 56 156 Q 60 172 72 182 L 74 192 Q 86 196 100 196 Q 114 196 126 192 L 128 182 Q 140 172 144 156 L 142 96 Q 140 80 130 74 Q 114 66 100 66 Q 86 66 70 74 Z"
          fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.95" strokeLinejoin="round"
        />

        {/* Arms */}
        <path d="M 52 80 Q 46 96 44 124 Q 45 146 50 168 L 60 168 Q 62 146 62 124 Q 64 96 66 80 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.85" />
        <path d="M 148 80 Q 154 96 156 124 Q 155 146 150 168 L 140 168 Q 138 146 138 124 Q 136 96 134 80 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.85" />
        {/* Forearms taper */}
        <path d="M 50 168 Q 49 178 52 188 L 60 188 Q 61 178 60 168 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.75" />
        <path d="M 150 168 Q 151 178 148 188 L 140 188 Q 139 178 140 168 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.75" />
        {/* Hands */}
        <ellipse cx="56" cy="194" rx="7" ry="9" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.7" />
        <ellipse cx="144" cy="194" rx="7" ry="9" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.7" />

        {/* Legs (thigh) */}
        <path d="M 70 198 Q 64 226 66 268 L 74 304 Q 82 308 86 304 L 92 268 Q 94 226 90 200 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.9" />
        <path d="M 130 198 Q 136 226 134 268 L 126 304 Q 118 308 114 304 L 108 268 Q 106 226 110 200 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.9" />
        {/* Knee circles */}
        <ellipse cx="80" cy="278" rx="10" ry="6" fill="rgba(34,211,238,0.12)" stroke="url(#rimLight)" strokeWidth="0.7" />
        <ellipse cx="120" cy="278" rx="10" ry="6" fill="rgba(34,211,238,0.12)" stroke="url(#rimLight)" strokeWidth="0.7" />
        {/* Calves */}
        <path d="M 74 304 Q 72 318 76 330 L 86 330 Q 88 318 86 304 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.8" />
        <path d="M 126 304 Q 128 318 124 330 L 114 330 Q 112 318 114 304 Z" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.8" />
        {/* Feet */}
        <ellipse cx="80" cy="334" rx="11" ry="6" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.75" />
        <ellipse cx="120" cy="334" rx="11" ry="6" fill="url(#bodyVolume)" stroke="url(#rimLight)" strokeWidth="0.75" />
      </g>

      {/* --- Skeleton (drawn over body, glowing) --- */}
      {side === "front" ? <FrontSkeleton /> : <BackSkeleton />}

      {/* --- Top rim highlight (directional light) --- */}
      <g fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.5">
        <path d="M 82 60 Q 100 54 118 60" />
        <path d="M 84 18 Q 100 14 116 18" />
      </g>

      {/* --- Interactive regions (tap or hold-to-ramp) --- */}
      {regions.map((r) => {
        const path = map[r.id];
        if (!path) return null;
        const committed = selected[r.id] || 0;
        // While ramping THIS region, show the ramp value instead
        const ramping = holdState && holdState.id === r.id;
        const display = ramping ? holdState.value : committed;
        const color = intensityColor(display);
        const isActive = activeId === r.id;
        const common = {
          fill: display > 0 ? color : "rgba(34,211,238,0.04)",
          stroke: ramping ? "#FFFFFF" : (isActive ? "#22D3EE" : (display > 0 ? color : "rgba(34,211,238,0.35)")),
          strokeWidth: ramping ? 2 : (isActive ? 1.6 : 0.9),
          opacity: display > 0 ? 0.92 : 0.7,
          style: {
            cursor: "pointer",
            transition: ramping ? "fill 80ms linear" : "fill 0.4s ease, stroke 0.3s ease",
            filter: display > 0
              ? `drop-shadow(0 0 ${ramping ? 14 : 8}px ${color}${ramping ? "DD" : "99"})`
              : isActive ? "drop-shadow(0 0 6px rgba(34,211,238,0.6))" : "none",
            touchAction: "none",
          },
          onPointerDown: (e) => handlers.down(r.id, e),
          onPointerUp: (e) => handlers.up(r.id, e),
          onPointerLeave: () => handlers.leave(r.id),
          onPointerCancel: () => handlers.leave(r.id),
          "data-testid": `pain-region-${r.id}`,
        };
        if (path.type === "ellipse") {
          return <ellipse key={r.id} cx={path.cx} cy={path.cy} rx={path.rx} ry={path.ry} {...common} />;
        }
        return (
          <rect
            key={r.id}
            x={path.x}
            y={path.y}
            width={path.w}
            height={path.h}
            rx={path.r}
            {...common}
          />
        );
      })}

      {/* --- Ramp-in-progress badge over the held region --- */}
      {holdState && (() => {
        const p = map[holdState.id];
        if (!p) return null;
        const cx = p.type === "ellipse" ? p.cx : p.x + p.w / 2;
        const cy = p.type === "ellipse" ? p.cy : p.y + p.h / 2;
        const c = intensityColor(holdState.value);
        return (
          <g style={{ pointerEvents: "none" }} filter="url(#strongGlow)">
            <circle cx={cx} cy={cy} r={14} fill={c} opacity="0.9" stroke="#FFFFFF" strokeWidth="1" />
            <text x={cx} y={cy + 4} textAnchor="middle" fontSize="14" fontWeight="700" fill="#03101F" fontFamily="Outfit, sans-serif">{holdState.value}</text>
          </g>
        );
      })()}
    </svg>
  );
}

const HOLD_THRESHOLD_MS = 280; // begin ramp after this
const RAMP_STEP_MS = 200; // increment every N ms during ramp

/** Tap = select; hold = ramp intensity 1→10, release to commit. */
function usePainGesture({ value, commit, onSelect }) {
  const [holdState, setHoldState] = useState(null); // { id, value }
  const holdTimeoutRef = useRef(null);
  const rampIntervalRef = useRef(null);
  const wasHoldRef = useRef(false);

  function clearTimers() {
    if (holdTimeoutRef.current) { clearTimeout(holdTimeoutRef.current); holdTimeoutRef.current = null; }
    if (rampIntervalRef.current) { clearInterval(rampIntervalRef.current); rampIntervalRef.current = null; }
  }

  useEffect(() => () => clearTimers(), []);

  const handlers = {
    down: (id) => {
      wasHoldRef.current = false;
      clearTimers();
      holdTimeoutRef.current = setTimeout(() => {
        wasHoldRef.current = true;
        let v = (value[id] || 0) + 1;
        if (v > 10) v = 10;
        setHoldState({ id, value: v });
        rampIntervalRef.current = setInterval(() => {
          setHoldState((s) => (s ? { ...s, value: s.value >= 10 ? 10 : s.value + 1 } : s));
        }, RAMP_STEP_MS);
      }, HOLD_THRESHOLD_MS);
    },
    up: (id) => {
      clearTimers();
      if (wasHoldRef.current && holdState && holdState.id === id) {
        commit(id, holdState.value);
        setHoldState(null);
      } else {
        onSelect(id);
      }
      wasHoldRef.current = false;
    },
    leave: (id) => {
      if (rampIntervalRef.current && holdState && holdState.id === id) {
        commit(id, holdState.value);
      }
      clearTimers();
      setHoldState(null);
      wasHoldRef.current = false;
    },
  };

  return { holdState, handlers };
}

/** Front/Back side toggle. */
function SideToggle({ side, onSide }) {
  return (
    <div className="bps-card p-1.5 flex items-center gap-1.5">
      {["front", "back"].map((s) => (
        <button
          key={s}
          data-testid={`scanner-side-${s}`}
          onClick={() => onSide(s)}
          className={`flex-1 py-2.5 rounded-2xl text-sm font-medium transition-all ${
            side === s
              ? "bg-[#22D3EE] text-[#03101F] shadow-[0_0_18px_rgba(34,211,238,0.4)]"
              : "text-white/70 hover:bg-white/5"
          }`}
        >
          {s === "front" ? "Front" : "Back"}
        </button>
      ))}
    </div>
  );
}

/** Intensity fine-tune panel for the currently selected region. */
function IntensityPanel({ activeId, activeLabel, activeIntensity, onSetIntensity }) {
  return (
    <AnimatePresence mode="wait">
      {activeId && (
        <motion.div
          key={activeId}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="bps-card bps-card-accent p-5 space-y-4"
          data-testid="intensity-panel"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="bps-eyebrow">Selected</div>
              <div className="font-display text-lg font-medium">{activeLabel}</div>
            </div>
            <div
              className="text-sm font-medium px-3 py-1 rounded-full"
              style={{
                background: activeIntensity > 0 ? intensityColor(activeIntensity) : "rgba(255,255,255,0.06)",
                color: activeIntensity > 0 ? "#03101F" : "#7C8FA8",
                boxShadow: activeIntensity > 0 ? `0 0 16px ${intensityColor(activeIntensity)}88` : "none",
              }}
            >
              {activeIntensity} · {intensityLabel(activeIntensity)}
            </div>
          </div>
          <Slider
            value={[activeIntensity]}
            max={10}
            step={1}
            onValueChange={(v) => onSetIntensity(activeId, v[0])}
            data-testid="intensity-slider"
          />
          <div className="flex justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8]">
            <span>None</span><span>Low</span><span>Mod</span><span>High</span><span>Severe</span>
          </div>
          <button
            onClick={() => onSetIntensity(activeId, 0)}
            data-testid="clear-region-button"
            className="text-xs text-[#22D3EE] font-medium"
          >
            Clear this area
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function BodyPainScanner({ value = {}, onChange }) {
  const [side, setSide] = useState("front");
  const [activeId, setActiveId] = useState(null);
  const regions = BODY_REGIONS[side];

  const activeIntensity = activeId ? value[activeId] || 0 : 0;
  const totalMarked = useMemo(() => Object.values(value).filter((v) => v > 0).length, [value]);

  function setIntensity(id, intensity) {
    const next = { ...value };
    if (intensity <= 0) delete next[id];
    else next[id] = intensity;
    onChange(next);
  }

  const { holdState, handlers } = usePainGesture({
    value,
    commit: (id, v) => { setIntensity(id, v); setActiveId(id); },
    onSelect: setActiveId,
  });

  const activeLabel = activeId
    ? [...BODY_REGIONS.front, ...BODY_REGIONS.back].find((r) => r.id === activeId)?.label
    : null;

  return (
    <div className="space-y-5" data-testid="body-pain-scanner">
      <SideToggle side={side} onSide={(s) => { setSide(s); setActiveId(null); }} />

      <div className="bps-card p-4">
        <BodySilhouette
          side={side}
          regions={regions}
          selected={value}
          activeId={activeId}
          holdState={holdState}
          handlers={handlers}
        />
        <div className="text-center text-[11px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-2 space-y-0.5">
          <div>{totalMarked} {totalMarked === 1 ? "area" : "areas"} marked</div>
          <div className="text-[#5F7891]">tap to select · hold to ramp intensity</div>
        </div>
      </div>

      <IntensityPanel
        activeId={activeId}
        activeLabel={activeLabel}
        activeIntensity={activeIntensity}
        onSetIntensity={setIntensity}
      />
    </div>
  );
}
